import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, RotateCcw, Cpu, Monitor, Keyboard, Laptop, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const CAT_TILES = [
  { slug: "gpus", name: "Graphics Cards", tag: "01", icon: Cpu, img: "https://images.pexels.com/photos/35569902/pexels-photo-35569902.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "cpus", name: "Processors", tag: "02", icon: Cpu, img: "https://images.pexels.com/photos/1432669/pexels-photo-1432669.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "monitors", name: "Displays", tag: "03", icon: Monitor, img: "https://images.pexels.com/photos/7858742/pexels-photo-7858742.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "peripherals", name: "Peripherals", tag: "04", icon: Keyboard, img: "https://images.unsplash.com/photo-1618384887929-16ec33fab9ef?crop=entropy&cs=srgb&fm=jpg&q=80&w=940" },
  { slug: "laptops", name: "Laptops", tag: "05", icon: Laptop, img: "https://images.pexels.com/photos/15393003/pexels-photo-15393003.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "desktops", name: "Prebuilts", tag: "06", icon: Cpu, img: "https://images.pexels.com/photos/30469973/pexels-photo-30469973.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const { isPrelaunch } = useSiteSettings();

  useEffect(() => {
    api.get("/products", { params: { limit: 8, sort: "featured" } }).then(({ data }) =>
      setFeatured(data.items.filter((p) => p.featured).slice(0, 8))
    );
    api.get("/products", { params: { limit: 8, sort: "newest" } }).then(({ data }) =>
      setNewArrivals(data.items.slice(0, 8))
    );
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative border-b border-zinc-800 overflow-hidden noise">
        <div className="absolute inset-0 scanlines" />
        <div className="container-tight relative py-16 md:py-24 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-6 space-y-6">
            <div className="flex items-center gap-3 font-mono text-xs tracking-widest text-[#FF5C00]">
              <span className="w-8 h-px bg-[#FF5C00]" />
              <span>/ INTERSTATE PC / TECHNOLOGY &amp; COMPONENTS NETWORK</span>
            </div>
            <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.95] tracking-tight">
              COMPUTERS<span className="text-[#FF5C00]">.</span><br />
              COMPONENTS<span className="text-[#FF5C00]">.</span><br />
              <span className="text-zinc-500">TECHNOLOGY.</span>
            </h1>
            <p className="text-zinc-400 max-w-lg leading-relaxed">
              For home, business, school and gaming. Computers, components, peripherals and accessories for the way you work, learn and play.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/catalog"><Button data-testid="hero-shop-btn"
                className="bg-[#FF5C00] hover:bg-[#FF7A33] text-white rounded-sm font-mono tracking-wider h-12 px-6 glow-orange">
                {isPrelaunch ? "BROWSE THE CATALOG" : "SHOP THE CATALOG"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button></Link>
              <Link to="/catalog?category=laptops"><Button data-testid="hero-laptops-btn" variant="outline"
                className="border-zinc-700 hover:border-[#00F0FF] hover:text-[#00F0FF] bg-transparent rounded-sm font-mono tracking-wider h-12 px-6">
                LAPTOPS &amp; DESKTOPS
              </Button></Link>
            </div>
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-zinc-800 max-w-lg">
              <div><div className="font-mono text-2xl font-bold text-white">HOME</div><div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">Everyday PCs</div></div>
              <div><div className="font-mono text-2xl font-bold text-white">WORK</div><div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">Business Ready</div></div>
              <div><div className="font-mono text-2xl font-bold text-[#FF5C00]">PLAY</div><div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">Gaming Gear</div></div>
            </div>
          </div>
          <div className="md:col-span-6 grid grid-cols-2 gap-3">
            <div className="tactical-border col-span-2 aspect-[16/9] bg-[#0a0a0c] border border-zinc-800 overflow-hidden">
              <img src="https://images.pexels.com/photos/30469973/pexels-photo-30469973.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Setup" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square bg-[#0a0a0c] border border-zinc-800 overflow-hidden">
              <img src="https://images.pexels.com/photos/35569902/pexels-photo-35569902.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="GPU" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square bg-[#0a0a0c] border border-zinc-800 overflow-hidden">
              <img src="https://images.unsplash.com/photo-1547394765-185e1e68f34e?crop=entropy&cs=srgb&fm=jpg&q=80&w=940"
                alt="Keyboard" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* PC Builder CTA */}
      <section data-testid="pc-builder-cta" className="relative border-b border-zinc-800 bg-[#08080b] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: "linear-gradient(#00F0FF 1px, transparent 1px), linear-gradient(90deg, #00F0FF 1px, transparent 1px)",
          backgroundSize: "48px 48px"
        }} />
        <div className="container-tight relative py-14 md:py-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8 space-y-5">
            <div className="flex items-center gap-3 font-mono text-xs tracking-widest text-[#00F0FF]">
              <span className="w-8 h-px bg-[#00F0FF]" />
              <span>// TOOLKIT / PC BUILDER</span>
            </div>
            <h2 className="font-display font-black text-3xl md:text-5xl leading-[1] tracking-tight">
              BUILD YOUR<br />
              PERFECT <span className="text-[#00F0FF]">PC</span><span className="text-[#FF5C00]">.</span>
            </h2>
            <p className="text-zinc-400 max-w-xl leading-relaxed">
              Plan your system, check component compatibility, and create a build that matches your needs and budget.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <a href="https://build.interstate-pc.com" data-testid="home-pc-builder-cta">
                <Button
                  className="bg-[#00F0FF] hover:bg-[#33F4FF] text-black rounded-sm font-mono tracking-wider h-12 px-6">
                  <Wrench className="w-4 h-4 mr-2" />START PC BUILDER <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
          <div className="md:col-span-4 hidden md:block">
            <div className="tactical-border aspect-square bg-[#0a0a0c] border border-zinc-800 relative overflow-hidden flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/10 via-transparent to-[#FF5C00]/10" />
              <Wrench className="w-24 h-24 text-[#00F0FF] relative" strokeWidth={1} />
              <div className="absolute bottom-3 left-3 font-mono text-[10px] tracking-widest text-zinc-500">
                / BUILD.INTERSTATE-PC.COM
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar — placeholder policies until configured */}
      <section className="border-b border-zinc-800">
        <div className="container-tight py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Cpu, label: "COMPUTERS & TECHNOLOGY", sub: "Broad category catalog" },
            { icon: ShieldCheck, label: "HOME · BUSINESS · SCHOOL", sub: "Built for every use" },
            { icon: Keyboard, label: "COMPONENTS & ACCESSORIES", sub: "Parts and peripherals" },
            { icon: Truck, label: isPrelaunch ? "ONLINE CATALOG" : "SECURE CHECKOUT",
              sub: isPrelaunch ? "Preview only" : "Stripe · SSL" },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-3">
              <f.icon className="w-5 h-5 text-[#FF5C00] mt-0.5" />
              <div>
                <div className="font-mono text-xs tracking-widest text-white">{f.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{f.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories bento */}
      <section className="container-tight py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono text-xs tracking-widest text-[#FF5C00] mb-2">// CATEGORIES</div>
            <h2 className="font-display font-black text-3xl md:text-4xl">SHOP BY DEPARTMENT</h2>
          </div>
          <Link to="/catalog" className="font-mono text-xs tracking-widest text-zinc-400 hover:text-[#FF5C00] hidden md:flex items-center gap-1">
            VIEW ALL <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {CAT_TILES.map((c) => (
            <Link key={c.slug} to={`/catalog?category=${c.slug}`} data-testid={`cat-tile-${c.slug}`}
              className="card-hover group relative aspect-square bg-[#0a0a0c] border border-zinc-800 overflow-hidden rounded-sm">
              <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-70 transition-opacity" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
              <div className="relative h-full flex flex-col justify-between p-4">
                <div className="font-mono text-[10px] tracking-widest text-[#FF5C00]">{c.tag}</div>
                <div>
                  <c.icon className="w-5 h-5 mb-2 text-[#00F0FF]" />
                  <div className="font-display font-bold text-white uppercase tracking-tight leading-tight">{c.name}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="container-tight pb-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono text-xs tracking-widest text-[#FF5C00] mb-2">// TOP GEAR</div>
            <h2 className="font-display font-black text-3xl md:text-4xl">FEATURED PRODUCTS</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Ticker */}
      <section className="border-y border-zinc-800 py-3 overflow-hidden">
        <div className="ticker flex gap-12 whitespace-nowrap font-mono text-sm tracking-widest text-zinc-500">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex gap-12">
              {["RTX 4090", "9800X3D", "OLED 240Hz", "DDR5-6000", "NVMe Gen4", "AM5", "WiFi 7", "8K POLLING", "RGB", "12VHPWR"].map((t, i) => (
                <span key={i} className="text-zinc-600">/ <span className="text-white ml-2">{t}</span></span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* New arrivals */}
      <section className="container-tight py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono text-xs tracking-widest text-[#00F0FF] mb-2">// FRESH DROPS</div>
            <h2 className="font-display font-black text-3xl md:text-4xl">NEW ARRIVALS</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {newArrivals.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>
    </div>
  );
}
